import {
    InputWrapperContext,
    TagsInput as MantineTagsInput,
    type TagsInputProps,
} from '@mantine/core';
import { use, useId, type CSSProperties, type ReactNode } from 'react';
import { cn } from '../../../utils/classname';
import './TagsInput.scss';

type TagsInputSize = 'sm' | 'default' | 'lg' | 'small' | 'medium' | 'large' | 'md';

type ResolvedTagsInputSize = 'sm' | 'default' | 'lg';

const sizeStyles: Record<
    ResolvedTagsInputSize,
    Pick<CSSProperties, 'padding' | 'fontSize' | 'minHeight'>
> = {
    sm: {
        padding: '0.25rem 0.5rem',
        fontSize: '0.8125rem',
        minHeight: '2rem',
    },
    default: {
        padding: '0.335rem 0.65rem',
        fontSize: '0.875rem',
        minHeight: '2.35rem',
    },
    lg: {
        padding: '0.5rem 0.9rem',
        fontSize: '1rem',
        minHeight: '3rem',
    },
};

function resolveTagsInputSize(size: TagsInputSize): ResolvedTagsInputSize {
    if (size === 'small') return 'sm';
    if (size === 'medium' || size === 'md') return 'default';
    if (size === 'large') return 'lg';
    return size;
}

export interface ITagsInputProps extends Omit<
    TagsInputProps,
    'size' | 'label' | 'description' | 'error' | 'classNames' | 'styles' | 'inputContainer'
> {
    size?: TagsInputSize;

    label?: ReactNode;
    labelClassName?: string;
    labelStyle?: CSSProperties;
    labelRight?: ReactNode;

    description?: ReactNode;
    descriptionBottom?: ReactNode;
    error?: ReactNode;

    width?: CSSProperties['width'];
    containerClassName?: string;
    containerStyle?: CSSProperties;
    inputClassName?: string;
}

// Mantine's PillsInput.Field takes aria-describedby only from this context and overwrites the prop.
function DescribedBy({ ids, children }: { ids?: string; children: ReactNode }) {
    const ctx = use(InputWrapperContext);
    return (
        <InputWrapperContext value={{ ...ctx, describedBy: ids }}>{children}</InputWrapperContext>
    );
}

const TagsInput = ({
    size = 'default',
    label,
    labelClassName,
    labelStyle,
    labelRight,
    description,
    descriptionBottom,
    error,
    width,
    containerClassName,
    containerStyle,
    inputClassName,
    id,
    ...mantineProps
}: ITagsInputProps) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    const descriptionId = `${inputId}-description`;
    const descriptionBottomId = `${inputId}-description-bottom`;
    const errorId = `${inputId}-error`;
    const describedBy =
        [
            description ? descriptionId : null,
            descriptionBottom ? descriptionBottomId : null,
            error ? errorId : null,
        ]
            .filter(Boolean)
            .join(' ') || undefined;
    const sizeStyle = sizeStyles[resolveTagsInputSize(size)];

    const labelElement = label ? (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.5rem',
            }}
        >
            <label
                htmlFor={inputId}
                className={cn('xv-tagsinput-label', labelClassName)}
                style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--color-text, currentColor)',
                    ...labelStyle,
                }}
            >
                {label}
            </label>
            {labelRight ? (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{labelRight}</span>
            ) : null}
        </div>
    ) : null;

    return (
        <div
            className={cn('xv-tagsinput-root', containerClassName)}
            style={{
                display: 'inline-flex',
                flexDirection: 'column',
                width: width ?? '100%',
                ...containerStyle,
            }}
        >
            {labelElement}
            {description && (
                <span
                    id={descriptionId}
                    className="xv-tagsinput-description"
                    style={{
                        fontSize: '0.85rem',
                        color: 'color-mix(in srgb, var(--color-text) 68%, transparent)',
                        marginBottom: '0.25rem',
                    }}
                >
                    {description}
                </span>
            )}
            <div className="xv-tagsinput-shell" data-error={error ? 'true' : undefined}>
                <MantineTagsInput
                    id={inputId}
                    error={error ? true : undefined}
                    withErrorStyles={false}
                    inputContainer={(children) => (
                        <DescribedBy ids={describedBy}>{children}</DescribedBy>
                    )}
                    classNames={{
                        input: cn('xv-tagsinput-input', inputClassName),
                        pill: 'xv-tagsinput-pill',
                        pillsList: 'xv-tagsinput-pillsList',
                    }}
                    styles={{
                        root: { width: '100%' },
                        input: {
                            minHeight: sizeStyle.minHeight,
                            padding: sizeStyle.padding,
                            fontSize: sizeStyle.fontSize,
                            borderRadius: '6px',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: 'var(--color-input-border, var(--color-border))',
                            backgroundColor:
                                'var(--color-input-bg, color-mix(in srgb, var(--color-background) 97%, var(--color-button-bg)))',
                            color: 'var(--color-text, currentColor)',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        pill: {
                            fontSize: '0.8125rem',
                            borderRadius: '4px',
                        },
                        pillsList: { gap: '0.35rem' },
                    }}
                    {...mantineProps}
                />
            </div>
            {descriptionBottom && (
                <span
                    id={descriptionBottomId}
                    className="xv-tagsinput-description-bottom"
                    style={{
                        fontSize: '0.85rem',
                        color: 'color-mix(in srgb, var(--color-text) 68%, transparent)',
                        marginTop: '0.25rem',
                    }}
                >
                    {descriptionBottom}
                </span>
            )}
            {error && (
                <span
                    id={errorId}
                    style={{
                        fontSize: '0.75rem',
                        color: 'var(--color-error, #ef4444)',
                        marginTop: '0.25rem',
                    }}
                >
                    {error}
                </span>
            )}
        </div>
    );
};

export default TagsInput;
